import React from "react";
import { Modal, Form, Input, Select, Switch, Button, Row, Col } from "antd";
import { DownOutlined } from "@ant-design/icons";

const { Option } = Select;

const AddDocumentModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  form,
  editingDocument = null
}) => {
  const documentExtensions = [
    { label: "PDF", value: "pdf" },
    { label: "Word Document", value: "doc" },
    { label: "Word Document (DOCX)", value: "docx" },
    { label: "Excel Spreadsheet", value: "xls" },
    { label: "Excel Spreadsheet (XLSX)", value: "xlsx" },
    { label: "PowerPoint", value: "ppt" },
    { label: "PowerPoint (PPTX)", value: "pptx" },
    { label: "JPEG Image", value: "jpg" },
    { label: "PNG Image", value: "png" },
    { label: "Text File", value: "txt" },
    { label: "CSV File", value: "csv" },
    { label: "ZIP Archive", value: "zip" },
    { label: "RAR Archive", value: "rar" }
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  return (
    <Modal
      title={editingDocument ? "Edit Document" : "Add Documents"}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: "20px" }}
        initialValues={{
          additionalFields: editingDocument?.additionalFields || []
        }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="documentTitle"
              label={<strong>Document Title</strong>}
              rules={[{ required: true, message: "Please enter document title" }]}
            >
              <Input placeholder="Enter document title" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="documentType"
              label={<strong>Allowed File Extensions</strong>}
              rules={[{ required: true, message: "Please select at least one file extension" }]}
            >
              <Select
                mode="multiple"
                placeholder="Select allowed file extensions"
                suffixIcon={<DownOutlined />}
                maxTagCount="responsive"
              >
                {documentExtensions.map(ext => (
                  <Option key={ext.value} value={ext.value}>{ext.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} align="middle">
          <Col span={8}>
            <strong>Max File Size (MB):</strong>
          </Col>
          <Col span={16}>
            <Form.Item
              name="maxFileSize"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(new Error('File size must be between 1 and 20 MB'));
                    }
                    const num = Number(value);
                    if (isNaN(num) || num < 1 || num > 20) {
                      return Promise.reject(new Error('File size must be between 1 and 20 MB'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              initialValue={10}
              style={{ marginBottom: 0 }}
            >
              <Input type="number" min={1} max={20} placeholder="10" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} align="middle" style={{ marginTop: "16px" }}>
          <Col span={6}>
            <strong>Mandatory:</strong>
          </Col>
          <Col span={6}>
            <Form.Item
              name="mandatory"
              valuePropName="checked"
              initialValue={false}
              style={{ marginBottom: 0 }}
            >
              <Switch size="small" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <strong>Verification:</strong>
          </Col>
          <Col span={6}>
            <Form.Item
              name="verificationRequired"
              valuePropName="checked"
              initialValue={false}
              style={{ marginBottom: 0 }}
            >
              <Switch size="small" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={<strong>Additional Fields</strong>}>
          <Form.List name="additionalFields">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={8} align="top">
                    <Col span={10}>
                      <Form.Item
                        {...restField}
                        name={[name, 'key']}
                        rules={[{ required: true, message: 'Field name required' }]}
                        style={{ marginBottom: '8px' }}
                      >
                        <Input placeholder="Field name (e.g., Passport Type)" />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item
                        {...restField}
                        name={[name, 'type']}
                        rules={[{ required: true, message: 'Field type required' }]}
                        style={{ marginBottom: '8px' }}
                      >
                        <Select placeholder="Select field type">
                          <Option value="text">Text</Option>
                          <Option value="number">Number</Option>
                          <Option value="date">Date</Option>
                          {/* <Option value="dropdown">Dropdown</Option>
                          <Option value="boolean">Yes/No</Option> */}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Form.Item style={{ marginBottom: '8px' }}>
                        <Button 
                          type="text" 
                          danger 
                          onClick={() => remove(name)}
                          style={{ fontSize: '1.2rem', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          ✕
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
                <Button 
                  type="dashed" 
                  onClick={() => add()} 
                  block
                  style={{ marginTop: '8px' }}
                >
                  + Add Field
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Row justify="end" gutter={16} style={{ marginTop: "30px" }}>
          <Col>
            <Button onClick={onClose}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button 
              type="primary" 
              onClick={handleSubmit}
              loading={isLoading}
            >
              {editingDocument ? "Update" : "Create"}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddDocumentModal;